import { FCAdditionalProps, getFormattedThousand, Tooltip } from "@datassential/platform-ui-lib";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import Title from "@components/Title";
import './Affinity.scss';
import BarPieChart from "@components/BarPieChart";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { FC, useId, useMemo } from "react";
import {
    AffinityAmong,
    getMetricBgColor,
    getMetricColor,
    Metric
} from "@models/Metric";
import Popup from "@components/Popup";
import { useCommonDataContext } from "@core/CommonDataContext";
import { getNthEnding } from "@core/utils/string";
import { ProfileAffinityRequest, useProfileAffinity } from "@core/api/useProfileAffinity";
import { IOption } from "@models";
import { SelectSingle } from "@components/Select";
import cn from "classnames";
import { useProfileContext } from "@pages/Profile/ProfileContext";
import { ProfileDataResponseItem } from "@core/api/useProfileHeader";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";

const METRICS_WITH_PERCENTILE = [Metric.LoveIt, Metric.LikeIt];

type AffinityProps = Omit<FCAdditionalProps, 'id'> & {
    id: ProfileDataResponseItem["id"];
    name: ProfileDataResponseItem["name"];
}

const Affinity: FC<AffinityProps> = ({
    id,
    name = "",
    testId = "profileAffinity",
    ...props
}) => {
    const tooltipId = useId();

    const {
        affinityAmong,
        metrics,
    } = useCommonDataContext();
    const {
        categoryId,
        amongThose,
        setAmongThose,
    } = useProfileContext();

    const { amongPayload } = useSelectAmongContext();

    const payload = useMemo(
        (): ProfileAffinityRequest => ({
            ...amongPayload,
            id,
            amongThose,
            categoryId,
        }),
        [amongPayload, id, amongThose, categoryId]
    );

    const {
        data: affinity,
        isLoading
    } = useProfileAffinity(payload);

    const metricsWithValues = useMemo(
        () => {
            return affinity.map((i) => {
                    const o = (metrics ?? []).find(o => o.id === i.metric);

                    return {
                        color: getMetricColor(o?.id),
                        barBg: getMetricBgColor(o?.id),
                        tooltipContent: METRICS_WITH_PERCENTILE.includes(o?.id ?? Metric.NoData)
                            ? `${getFormattedThousand(i.norm)}${getNthEnding(i.norm ?? 0)} Percentile`
                            : undefined,
                        value: i.raw ?? 0,
                        title: o?.name ?? '',
                        id: o?.id ?? o?.name ?? '',
                    };
                });
        },
        [affinity, metrics],
    );

    const loveItMetricData = useMemo(
        () => {
            return affinity.find(i => i.metric === Metric.LoveIt);
        },
        [affinity],
    );

    const amongOptions = useMemo(
        (): IOption<AffinityAmong, string>[] => {
            return (affinityAmong ?? []).map((i) => ({
                id: i.id as AffinityAmong,
                title: i.name ?? '',
            }));
        },
        [affinityAmong],
    );

    return (
        <ContentWidget
            className={cn(
                "ProfileAffinity",
                "appears-left", "del-2",
            )}
            isLoading={isLoading}
            testId={testId}
            {...props}
        >
            <div className="ContentWidget__header ProfileAffinity__header">
                <Title testId="textProfileAffinityTitle">AFFINITY</Title>
                <SelectSingle
                    value={amongThose}
                    setValue={setAmongThose}
                    options={amongOptions}
                    label={(
                        <>
                            <AiOutlineInfoCircle
                                data-tooltip-id={tooltipId}
                                className="ProfileAffinity__info-icon"
                                data-testid="iconProfileAffinityAmong"
                            />
                            <span data-testid="textProfileAffinityAmong">AMONG:</span>
                        </>
                    )}
                    testId="selectProfileAffinityAmong"
                />
                <Popup>
                    <Tooltip
                        id={tooltipId}
                        place={"top-start"}
                    >
                        <div data-testid="textProfileAffinityAmongTooltip">
                            <b>{loveItMetricData?.raw}% of consumers know &amp; love {name.toUpperCase()}</b> <br/>
                            all numbers are based on consumers with an opinion
                        </div>
                    </Tooltip>
                </Popup>
            </div>
            <BarPieChart
                className="ProfileAffinity__content"
                values={metricsWithValues}
                testId="blockProfileAffinityChart"
            />
        </ContentWidget>
    );
}

export default Affinity;